import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Link } from "react-router-dom";
import AppDataGrid from "../components/AppDataGrid";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import {
  FaPlus,
  FaMinus,
  FaUser,
  FaLayerGroup,
} from "react-icons/fa";
import {
  FiEye,
  FiPaperclip,
  FiCopy,
  FiEdit2,
  FiTrash2,
  FiPlusCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { BASE_URL, SCRAPING_BASE_URL } from "../utils";
import {
  BOROUGH_OPTIONS,
  buildPdfLink,
  confidencePill,
  formatConfidenceLabel,
  formatDate,
  formatMoney,
  safeText,
} from "./acrisLegacyUtils";

dayjs.extend(relativeTime);

ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

function TabButton({ active, icon: Icon, children, onClick }) {
  const base = "ui-btn ui-tab";
  const styles = active ? "ui-tab--active" : "ui-tab--inactive";
  return (
    <button type="button" onClick={onClick} className={`${base} ${styles}`}>
      {Icon && <Icon size={12} className="tab-icon" />}
      {children}
    </button>
  );
}

const DEFAULT_FILTERS = {
  borough: "",
  search: "",
  from: "",
  to: "",
  confidence: "",
};

const statusBadgeMap = {
  approved: "badge-pill badge-green",
  rejected: "badge-pill badge-rose",
  processing: "badge-pill badge-amber",
  pending: "badge-pill badge-amber",
  na: "badge-pill badge-neutral",
};

const DEFAULT_NOTE_FORM = {
  id: null,
  leadId: null,
  note: "",
  status: "Approved",
  sub_status: "NA",
  remark: "NA",
  label: "NA",
  followUp_date: "",
  pdf_url: "",
  created_at: "",
  updated_at: "",
};

function stripHtml(html) {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  } catch {
    return String(html).replace(/<[^>]+>/g, "");
  }
}

function sanitizeHtmlForView(html) {
  if (!html) return "";
  let s = String(html).replace(/<(script|style)[\s\S]*?>[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/\son\w+="[^"]*"/gi, "");
  s = s.replace(/\son\w+='[^']*'/gi, "");
  return s;
}

function isComplexHtml(html) {
  if (!html) return false;
  const lowered = String(html).toLowerCase();
  const tagCount = (String(html).match(/<\w+/g) || []).length;
  if (tagCount > 10) return true;
  if (lowered.includes("startfragment")) return true;
  if (lowered.includes("<div") || lowered.includes("<table") || lowered.includes("<img")) return true;
  if (String(html).length > 1000) return true;
  return false;
}

function firstNWords(text, n = 20) {
  if (!text) return "";
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  return words.slice(0, n).join(" ");
}

async function copyToClipboard(text, successMsg = "Copied", failMsg = "Copy failed") {
  const payload = text == null ? "" : String(text);

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(payload);
      toast.success(successMsg);
      return true;
    } catch (err) {
      console.warn("clipboard.writeText failed, fallback:", err);
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = payload;
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (ok) {
      toast.success(successMsg);
      return true;
    }

    toast.error(failMsg);
    return false;
  } catch (err) {
    console.error("Fallback copy failed:", err);
    toast.error(failMsg);
    return false;
  }
}

function getStatusClass(status) {
  const key = String(status || "NA").trim().toLowerCase();
  return statusBadgeMap[key] || "badge-pill badge-neutral";
}

function getLabelClass(label) {
  const normalized = String(label || "").trim().toUpperCase();
  if (normalized === "COLD") return "ag-label-pill ag-label-pill--cold";
  if (normalized === "HOT") return "ag-label-pill ag-label-pill--hot";
  if (normalized === "WARM") return "ag-label-pill ag-label-pill--warm";
  return "ag-label-pill ag-label-pill--na";
}

function buildNotePdfHref(pdfUrl) {
  if (!pdfUrl) return null;
  const s = String(pdfUrl).trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  const base = SCRAPING_BASE_URL || BASE_URL;
  const path = s.startsWith("/") ? s : `/${s}`;
  return `${base}${path}`;
}

function getLatestNote(notes) {
  if (!Array.isArray(notes) || notes.length === 0) return null;

  const sorted = [...notes].sort((a, b) => {
    const aTs = dayjs(a?.updated_at || a?.created_at || a?.followUp_date || 0).valueOf();
    const bTs = dayjs(b?.updated_at || b?.created_at || b?.followUp_date || 0).valueOf();
    return bTs - aTs;
  });

  return sorted[0] || null;
}

function normalizeLegacyRow(row) {
  const notes = Array.isArray(row?.notes) ? row.notes : [];
  const latestNote = getLatestNote(notes);

  const backendReview = String(row?.doc_review_status || "").trim().toLowerCase();
  const backendStatus = String(row?.status || row?.note_status || "").trim();
  const backendSubStatus = String(row?.sub_status || "").trim();
  const backendRemark = String(row?.remark || "").trim();
  const backendLabel = String(row?.label || "").trim();

  let resolvedStatus = "NA";
  if (latestNote?.status && String(latestNote.status).trim()) {
    resolvedStatus = String(latestNote.status).trim();
  } else if (backendStatus) {
    resolvedStatus = backendStatus;
  } else if (backendReview === "rejected") {
    resolvedStatus = "Rejected";
  } else if (backendReview === "approved") {
    resolvedStatus = "Approved";
  }

  const resolvedSubStatus =
    (latestNote?.sub_status && String(latestNote.sub_status).trim()) ||
    backendSubStatus ||
    "NA";

  const resolvedRemark =
    (latestNote?.remark && String(latestNote.remark).trim()) ||
    backendRemark ||
    "NA";

  const resolvedLabel =
    (latestNote?.label && String(latestNote.label).trim()) ||
    backendLabel ||
    "NA";

  const latestNoteHtml =
    (latestNote?.note && String(latestNote.note).trim()) ||
    row?.last_note ||
    "";

  const latestNotePlain =
    stripHtml(latestNoteHtml) ||
    latestNote?.plain_text ||
    stripHtml(row?.note_update_text || "") ||
    "";

  const noteUpdate =
    latestNote?.updated_at ||
    latestNote?.created_at ||
    latestNote?.followUp_date ||
    row?.note_update ||
    null;

  return {
    ...row,
    notes,
    status: resolvedStatus,
    note_status: resolvedStatus,
    sub_status: resolvedSubStatus,
    remark: resolvedRemark,
    label: resolvedLabel,
    last_note: latestNoteHtml,
    last_note_plain: latestNotePlain,
    note_update: noteUpdate,
    note_update_text: latestNotePlain || stripHtml(row?.note_update_text || ""),
    latest_date: latestNote?.followUp_date || row?.latest_date || null,
    followUp_date: latestNote?.followUp_date || row?.followUp_date || null,
    updated_date: row?.updated_date || null,
    read_status: latestNote?.read_status ?? row?.read_status ?? null,
  };
}

function NotePreviewCell({
  params,
  rawHtml,
  plainText,
  pdfHref,
  expandedCells,
  setExpandedCells,
  onShowHtml,
}) {
  const row = params.data;
  const key = `${row.id}-last_note`;
  const expanded = !!expandedCells[key];
  const safePlain = String(plainText || "").trim();
  const complex = isComplexHtml(rawHtml);
  const words = safePlain.split(/\s+/).filter(Boolean);
  const isLong = words.length > 6 || safePlain.length > 60;

  if (!safePlain && !pdfHref) {
    return (
      <div className="w-full flex items-center justify-start">
        <span className="text-[11px] text-[var(--fg-muted)]">No note</span>
      </div>
    );
  }

  if (complex) {
    const preview = expanded ? safePlain : firstNWords(safePlain, 8);

    return (
      <div
        className="w-full flex flex-col items-start justify-center text-left py-1"
        style={{ minHeight: 52, wordBreak: "break-word" }}
      >
        <div className="w-full text-[11px] leading-4 mb-1 whitespace-pre-wrap break-words">
          {preview || "HTML note"}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isLong && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedCells((prev) => ({ ...prev, [key]: !expanded }));
                try {
                  params.api.refreshCells({
                    rowNodes: [params.node],
                    columns: [params.column],
                    force: true,
                  });
                  params.api.resetRowHeights();
                } catch {}
              }}
              className="text-[var(--accent)] text-[10px]"
              type="button"
            >
              {expanded ? "View Less" : "View More"}
            </button>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowHtml(rawHtml);
            }}
            title="View full HTML"
            className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
            type="button"
          >
            <FiEye size={15} className="text-[var(--accent)]" />
          </button>

          {pdfHref && (
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-[var(--accent)] underline"
              title="Open attached PDF"
            >
              <FiPaperclip size={13} /> View PDF
            </a>
          )}

          {safePlain && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(safePlain, "Copied plain text", "Copy failed");
              }}
              title="Copy plain text"
              className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
              type="button"
            >
              <FiCopy size={14} className="text-[var(--fg)]" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const shownText = expanded || !isLong ? safePlain : `${words.slice(0, 6).join(" ")}...`;

  return (
    <div
      className="w-full flex flex-col items-start justify-center text-left py-1"
      style={{ minHeight: 52, wordBreak: "break-word" }}
    >
      <div className="w-full text-[11px] leading-4 mb-1 whitespace-pre-wrap break-words">
        {shownText || "—"}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isLong && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpandedCells((prev) => ({ ...prev, [key]: !expanded }));
              try {
                params.api.refreshCells({
                  rowNodes: [params.node],
                  columns: [params.column],
                  force: true,
                });
                params.api.resetRowHeights();
              } catch {}
            }}
            className="text-[var(--accent)] text-[10px]"
            type="button"
          >
            {expanded ? "View Less" : "View More"}
          </button>
        )}

        {pdfHref && (
          <a
            href={pdfHref}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-[var(--accent)] underline"
            title="Open attached PDF"
          >
            <FiPaperclip size={13} /> View PDF
          </a>
        )}

        {safePlain && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(safePlain, "Copied plain text", "Copy failed");
            }}
            title="Copy plain text"
            className="p-1 rounded hover:bg-[var(--surface-muted)] flex items-center justify-center"
            type="button"
          >
            <FiCopy size={14} className="text-[var(--fg)]" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AcrisLegacyLeads({ initialTab = "single", lockTab = false }) {
  const gridRef = useRef(null);
  const editEditorRef = useRef(null);

  const [tab, setTab] = useState(initialTab);
  const [tabCounts, setTabCounts] = useState({ single: 0, multiple: 0 });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [openAddress, setOpenAddress] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [sortBy, setSortBy] = useState("obituary_date_of_death");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [expandedCells, setExpandedCells] = useState({});
  const [expandedSingleId, setExpandedSingleId] = useState(null);
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlModalContent, setHtmlModalContent] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [noteModalMode, setNoteModalMode] = useState("edit");
  const [editingNote, setEditingNote] = useState(DEFAULT_NOTE_FORM);

  const formatAddress = (row) => {
    if (!row) return "";
    const house = String(
      row.property_house_number || row.house_number || row.house_no || ""
    ).trim();
    const street = String(
      row.property_street_name || row.street_name || row.street || ""
    ).trim();
    const borough = String(row.property_borough_name || row.borough || "").trim();
    const parts = [house, street, borough].filter(Boolean);
    const formatted = parts.join(", ");
    return formatted || String(row.property_full_address || "").trim();
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    const str = String(text);
    if (str.length <= limit) return str;
    return `${str.slice(0, limit)}...`;
  };

  useEffect(() => {
    if (!openAddress) return;

    const onDocMouseDown = (e) => {
      if (!openAddress?.anchorEl) return;
      if (!openAddress.anchorEl.contains(e.target)) {
        setOpenAddress(null);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenAddress(null);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openAddress]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    setExpandedSingleId(null);

    if (tab === "multiple") {
      setSortBy("latest_date");
      setSortDir("desc");
    } else {
      setSortBy("obituary_date_of_death");
      setSortDir("desc");
    }
  }, [tab]);

  useEffect(() => {
    if (!lockTab) return;
    if (tab !== initialTab) setTab(initialTab);
  }, [lockTab, initialTab, tab]);

  const getTooltipStyle = (rect) => {
    if (!rect) return { top: 0, left: 0 };
    const width = 380;
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
    return { top: rect.bottom + 8, left, width };
  };

  const applyFilters = () => {
    setApplied(filters);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleEnterApply = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    applyFilters();
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const refreshCounts = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/acris-legacy/leads/counts`, {
        params: applied,
      });
      setTabCounts({
        single: res?.data?.single ?? 0,
        multiple: res?.data?.multiple ?? 0,
      });
    } catch (err) {
      console.error("AcrisLegacy counts error", err);
      setTabCounts({ single: 0, multiple: 0 });
    }
  };

  useEffect(() => {
    refreshCounts();
  }, [applied]);

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      setError(null);

      try {
        const endpoint =
          tab === "multiple"
            ? "/api/acris-legacy/leads/multiple"
            : "/api/acris-legacy/leads/single";

        const res = await axios.get(`${BASE_URL}${endpoint}`, {
          params: {
            ...applied,
            page: pagination.page,
            limit: pagination.limit,
            sortBy,
            sortDir,
          },
        });

        const nextRows = ((res.data && res.data.data) || []).map((row) =>
          tab === "single" ? normalizeLegacyRow(row) : row
        );

        setRows(nextRows);
        setPagination((res.data && res.data.pagination) || pagination);
      } catch (err) {
        console.error("AcrisLegacy leads error", err);
        setRows([]);
        setPagination((p) => ({ ...p, total: 0, totalPages: 1 }));
        setError("Failed to load probate leads.");
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [applied, pagination.page, pagination.limit, sortBy, sortDir, tab, detailRefresh]);

  const refreshCurrentData = async () => {
    setDetailRefresh((v) => v + 1);
    await refreshCounts();
  };

  const showHtmlContent = (html) => {
    setHtmlModalContent(html || "");
    setHtmlModalOpen(true);
  };

  const openAddNoteModal = (leadRow) => {
    setNoteModalMode("add");
    setEditingNote({
      ...DEFAULT_NOTE_FORM,
      leadId: leadRow?.id || null,
      status: leadRow?.status || "Approved",
      sub_status: leadRow?.sub_status || "NA",
      remark: leadRow?.remark || "NA",
      label: leadRow?.label || "NA",
      followUp_date: leadRow?.followUp_date
        ? dayjs(leadRow.followUp_date).format("YYYY-MM-DD")
        : "",
    });
    setEditModalOpen(true);

    setTimeout(() => {
      if (editEditorRef.current) {
        editEditorRef.current.innerHTML = "";
        editEditorRef.current.focus();
      }
    }, 60);
  };

  const openEditNoteModal = (leadId, note) => {
    setNoteModalMode("edit");
    setEditingNote({
      id: note?.id || null,
      leadId: leadId || null,
      note: note?.note || "",
      status: note?.status || "Approved",
      sub_status: note?.sub_status || "NA",
      remark: note?.remark || "NA",
      label: note?.label || "NA",
      followUp_date: note?.followUp_date
        ? dayjs(note.followUp_date).format("YYYY-MM-DD")
        : "",
      pdf_url: note?.pdf_url || "",
      created_at: note?.created_at || "",
      updated_at: note?.updated_at || "",
    });
    setEditModalOpen(true);

    setTimeout(() => {
      if (editEditorRef.current) {
        editEditorRef.current.innerHTML = note?.note || "";
        editEditorRef.current.focus();
      }
    }, 60);
  };

  const closeNoteModal = () => {
    if (editSaving) return;
    setEditModalOpen(false);
    setEditingNote(DEFAULT_NOTE_FORM);
    setNoteModalMode("edit");
  };

  const handleSaveNoteModal = async () => {
    if (!editingNote?.leadId) {
      toast.error("Lead id not found.");
      return;
    }

    const html = editEditorRef.current
      ? editEditorRef.current.innerHTML
      : editingNote?.note || "";

    if (!String(html || "").trim()) {
      toast.error("Note cannot be empty.");
      return;
    }

    const payload = {
      note: html,
      plain_text: stripHtml(html),
      status: editingNote.status || "Approved",
      sub_status: editingNote.sub_status || "NA",
      remark: editingNote.remark || "NA",
      label: editingNote.label || "NA",
      followUp_date: editingNote.followUp_date
        ? dayjs(editingNote.followUp_date).toISOString()
        : null,
      pdf_url: editingNote.pdf_url || null,
    };

    setEditSaving(true);

    try {
      if (noteModalMode === "add") {
        await axios.post(
          `${BASE_URL}/api/acris-legacy/leads/${editingNote.leadId}/notes`,
          payload
        );
        toast.success("Note added successfully.");
      } else {
        if (!editingNote?.id) {
          toast.error("Note id not found.");
          return;
        }

        await axios.put(
          `${BASE_URL}/api/acris-legacy/leads/${editingNote.leadId}/notes/${editingNote.id}`,
          payload
        );
        toast.success("Note updated successfully.");
      }

      closeNoteModal();
      await refreshCurrentData();
    } catch (err) {
      console.error("Save legacy note error", err);
      toast.error(err?.response?.data?.message || "Failed to save note.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteNote = async (leadId, noteId) => {
    if (!leadId || !noteId) return;
    if (!window.confirm("Delete this note?")) return;

    try {
      await axios.delete(
        `${BASE_URL}/api/acris-legacy/leads/${leadId}/notes/${noteId}`
      );
      toast.success("Note deleted successfully.");
      await refreshCurrentData();
    } catch (err) {
      console.error("Delete legacy note error", err);
      toast.error(err?.response?.data?.message || "Failed to delete note.");
    }
  };

  const SingleDetailCellRenderer = ({ data }) => {
    const notes = Array.isArray(data?.notes) ? [...data.notes] : [];
    const sortedNotes = notes.sort((a, b) => {
      const aTs = dayjs(a?.updated_at || a?.created_at || a?.followUp_date || 0).valueOf();
      const bTs = dayjs(b?.updated_at || b?.created_at || b?.followUp_date || 0).valueOf();
      return bTs - aTs;
    });

    return (
      <div
        className="p-3 border-t"
        style={{
          whiteSpace: "normal",
          background: "var(--surface)",
          color: "var(--fg)",
          borderColor: "var(--border)",
        }}
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="mb-2 text-xs">
              <strong>Sub-Status:</strong> {data?.sub_status || "NA"}
            </div>
            <div className="mb-2 text-xs">
              <strong>Remark:</strong> {data?.remark || "NA"}
            </div>
            <div className="mb-2 text-xs">
              <strong>Label:</strong> {data?.label || "NA"}
            </div>
          </div>

          {/* <button
            type="button"
            onClick={() => openAddNoteModal(data)}
            className="ui-btn ui-btn-primary ui-btn-compact inline-flex items-center gap-2"
          >
            <FiPlusCircle size={14} />
            Add Note
          </button> */}
        </div>

        <div className="text-xs font-semibold mb-1">Notes:</div>

        {sortedNotes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[var(--border)] p-4 text-[11px] text-[var(--fg-muted)]">
            No notes found.
          </div>
        ) : (
          <div className="mt-1 space-y-3">
            {sortedNotes.map((note, idx) => {
              const rawHtml = note?.note || "";
              const plain = stripHtml(rawHtml);
              const pdfHref = buildNotePdfHref(note?.pdf_url);
              const noteStatus = String(note?.status || "").toLowerCase();

              return (
                <div
                  key={note?.id || idx}
                  className={`text-sm pl-2 border-l-4 ${
                    noteStatus === "approved"
                      ? "border-green-500"
                      : noteStatus === "rejected"
                      ? "border-rose-500"
                      : "border-slate-300"
                  }`}
                >
                  <div className="text-gray-800 whitespace-pre-line break-words">
                    {plain || "—"}
                  </div>

                  {pdfHref ? (
                    <div className="mt-1 mb-1 flex items-center">
                      <a
                        href={pdfHref}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 underline flex items-center gap-1"
                      >
                        <FiPaperclip size={12} />
                        <span>View Attached PDF</span>
                      </a>
                    </div>
                  ) : null}

                  <div className="text-[11px] text-gray-500 mt-2 flex items-center justify-between gap-2 flex-wrap">
                    <span>
                      {note?.updated_at || note?.created_at
                        ? dayjs(note.updated_at || note.created_at).format("MMM D, YYYY h:mm A")
                        : ""}
                    </span>

                    <div className="flex items-center gap-3 flex-wrap">
                      <span>
                        {note?.status || "NA"} / {note?.sub_status || "NA"}
                      </span>

                      <button
                        type="button"
                        onClick={() => openEditNoteModal(data?.id, note)}
                        className="text-indigo-600 text-xs inline-flex items-center gap-1"
                      >
                        <FiEdit2 size={13} />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteNote(data?.id, note?.id)}
                        className="text-red-500 text-xs inline-flex items-center gap-1"
                      >
                        <FiTrash2 size={13} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const DetailCellRenderer = ({ data }) => {
    const groupKey = data?.group_key;
    const [childRows, setChildRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [approvingId, setApprovingId] = useState(null);

    const fetchChildren = async () => {
      if (!groupKey) return;
      setBusy(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/acris-legacy/leads/multiple/${encodeURIComponent(groupKey)}`,
          { params: applied }
        );
        setChildRows((res?.data?.data || []).map(normalizeLegacyRow));
      } catch (err) {
        console.error("AcrisLegacy child rows error", err);
        setChildRows([]);
      } finally {
        setBusy(false);
      }
    };

    useEffect(() => {
      fetchChildren();
    }, [groupKey, detailRefresh]);

    const approveRecord = async (id) => {
      if (!id) return;
      if (!window.confirm("Approve this record and remove other duplicates from review?")) return;
      setApprovingId(id);
      try {
        const res = await axios.post(`${BASE_URL}/api/acris-legacy/leads/${id}/approve`);
        if (res?.data?.message) toast.success(res.data.message);
        setDetailRefresh((v) => v + 1);
        await refreshCounts();
      } catch (err) {
        console.error("Approve error", err);
        toast.error(err?.response?.data?.message || "Failed to approve record.");
      } finally {
        setApprovingId(null);
      }
    };

    const rejectRecord = async (id) => {
      if (!id) return;
      if (!window.confirm("Disapprove this record? It will be hidden from Probate Leads.")) return;
      setApprovingId(id);
      try {
        const res = await axios.post(`${BASE_URL}/api/acris-legacy/leads/${id}/reject`);
        if (res?.data?.message) toast.success(res.data.message);
        setDetailRefresh((v) => v + 1);
        await refreshCounts();
      } catch (err) {
        console.error("Reject error", err);
        toast.error(err?.response?.data?.message || "Failed to disapprove record.");
      } finally {
        setApprovingId(null);
      }
    };

    return (
      <div className="p-3 text-xs bg-[var(--surface-muted)]">
        <div className="text-[11px] uppercase text-[var(--fg-muted)] mb-2">
          {busy ? "Loading records…" : `${childRows.length} record(s)`}
        </div>
        <div className="overflow-auto">
          <table className="w-full text-left text-[12px] border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Date of Death</th>
                <th className="px-2 py-1.5">Published</th>
                <th className="px-2 py-1.5">Address</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Sub Status</th>
                <th className="px-2 py-1.5">Remark</th>
                <th className="px-2 py-1.5">Label</th>
                <th className="px-2 py-1.5">ACRIS Doc ID</th>
                <th className="px-2 py-1.5">Match</th>
                <th className="px-2 py-1.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {childRows.length === 0 && (
                <tr>
                  <td className="px-2 py-2 text-slate-400" colSpan={11}>
                    No records found.
                  </td>
                </tr>
              )}
              {childRows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">
                    <Link
                      to={`/acris-legacy/leads/${row.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:underline"
                    >
                      {safeText(row.full_name)}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">{formatDate(row.obituary_date_of_death)}</td>
                  <td className="px-2 py-1.5">{formatDate(row.obituary_published_date)}</td>
                  <td className="px-2 py-1.5">{formatAddress(row)}</td>
                  <td className="px-2 py-1.5">{row.status || "NA"}</td>
                  <td className="px-2 py-1.5">{row.sub_status || "NA"}</td>
                  <td className="px-2 py-1.5">{row.remark || "NA"}</td>
                  <td className="px-2 py-1.5">{row.label || "NA"}</td>
                  <td className="px-2 py-1.5">{safeText(row.acris_document_id)}</td>
                  <td className="px-2 py-1.5">
                    <span
                      className={`${confidencePill(row.match_confidence)} match-pill`}
                      title={formatConfidenceLabel(row.match_confidence)}
                    >
                      {formatConfidenceLabel(row.match_confidence)}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    {String(row.doc_review_status || "").toLowerCase() === "approved" ? (
                      <button
                        className="ui-btn ui-btn-danger ui-btn-compact"
                        onClick={() => rejectRecord(row.id)}
                        disabled={approvingId === row.id}
                      >
                        Disapprove
                      </button>
                    ) : (
                      <button
                        className="ui-btn ui-btn-primary ui-btn-compact"
                        onClick={() => approveRecord(row.id)}
                        disabled={approvingId === row.id}
                      >
                        {approvingId === row.id ? "Approving..." : "Approve"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTruncated = (params, text) => {
    const row = params.data;
    const field = params.colDef.field;
    const key = `${row.id}-${field}`;
    const expanded = !!expandedCells[key];
    const str = typeof text === "string" ? text : text ?? "";

    if (str.length <= 40) {
      return <span className="text-xs whitespace-pre-wrap">{str || "NA"}</span>;
    }

    const shown = expanded ? str : `${str.slice(0, 40)}...`;

    return (
      <span className="text-xs" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {shown}{" "}
        <button
          className="text-[var(--accent)] underline text-[10px]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpandedCells((prev) => ({ ...prev, [key]: !expanded }));
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: [params.column],
              force: true,
            });
            params.api.resetRowHeights();
          }}
        >
          {expanded ? "View Less" : "View More"}
        </button>
      </span>
    );
  };

  const toggleSingleDetail = (params) => {
    const api = params.api;
    const node = params.node;
    const rowId = node?.data?.id;
    const willOpen = !(node && node.expanded);

    api.forEachNode((n) => {
      if (n && n.expanded) n.setExpanded(false);
    });

    if (willOpen) {
      node.setExpanded(true);
      setExpandedSingleId(rowId);
    } else {
      setExpandedSingleId(null);
    }

    api.refreshCells({ force: true });
    api.resetRowHeights();
  };

  const singleColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "full_name",
        minWidth: 190,
        flex: 1,
        cellRenderer: (p) => {
          const id = p?.data?.id;
          if (!id) return <span className="block truncate">{safeText(p.value)}</span>;
          return (
            <Link
              to={`/acris-legacy/leads/${id}`}
              target="_blank"
              rel="noreferrer"
              className="block w-full truncate text-left text-sm font-normal text-indigo-600 hover:underline"
              title={p.value || ""}
              onClick={(e) => e.stopPropagation()}
            >
              {safeText(p.value)}
            </Link>
          );
        },
        cellStyle: { paddingLeft: 18 },
      },
      {
        headerName: "Date of Death",
        field: "obituary_date_of_death",
        width: 130,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Published",
        field: "obituary_published_date",
        width: 120,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Address",
        field: "property_full_address",
        minWidth: 240,
        flex: 1,
        valueGetter: (p) => formatAddress(p.data),
        cellRenderer: (p) => {
          const full = String(p.value || "").trim();
          const isLong = full.length > 20;
          const short = truncateText(full, 20);
          const rowId =
            p?.node?.id != null ? p.node.id : p?.node?.rowIndex != null ? p.node.rowIndex : null;
          const open = rowId != null && openAddress && openAddress.id === rowId;

          if (!full) return <span className="text-slate-400">-</span>;

          return (
            <div className="w-full relative">
              <div className="flex items-center gap-2 w-full">
                <span className="block flex-1 min-w-0 truncate text-sm" style={{ userSelect: "text" }}>
                  {isLong ? short : full}
                </span>
                {isLong && (
                  <button
                    type="button"
                    className="shrink-0 text-[10px] text-blue-600 hover:text-blue-700"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const anchorEl = e.currentTarget;
                      const rect = anchorEl.getBoundingClientRect();
                      if (open) {
                        setOpenAddress(null);
                      } else {
                        setOpenAddress({ id: rowId, rect, text: full, anchorEl });
                      }
                    }}
                  >
                    {open ? "Less" : "View more"}
                  </button>
                )}
              </div>
            </div>
          );
        },
      },
      { headerName: "Borough", field: "property_borough_name", width: 120 },
      {
        headerName: "Match",
        field: "match_confidence",
        width: 120,
        cellRenderer: (p) => (
          <span
            className={`${confidencePill(p.value)} match-pill`}
            title={formatConfidenceLabel(p.value)}
          >
            {formatConfidenceLabel(p.value)}
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "status",
        width: 160,
        minWidth: 160,
        sortable: true,
        filter: true,
        cellRenderer: (params) => {
          const isExpanded = !!params.node?.expanded;
          const displayStatus = params.value || "NA";

          return (
            <div className="w-full flex items-center justify-center gap-2">
              <span className={getStatusClass(displayStatus)}>
                {displayStatus}
              </span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSingleDetail(params);
                }}
                className={[
                  "w-6 h-6 rounded-full flex items-center justify-center",
                  "border border-[var(--border)] shadow-sm transition",
                  isExpanded
                    ? "bg-rose-600 text-white hover:bg-rose-700"
                    : "bg-[var(--accent)] text-white hover:bg-[var(--accent-strong)]",
                ].join(" ")}
                title={isExpanded ? "Collapse" : "Expand"}
                type="button"
              >
                {isExpanded ? <FaMinus size={10} /> : <FaPlus size={10} />}
              </button>
            </div>
          );
        },
      },
      {
        headerName: "Last Note",
        field: "last_note",
        width: 320,
        minWidth: 300,
        sortable: false,
        filter: true,
        cellRenderer: (params) => {
          const row = params.data;
          const latestNote = getLatestNote(row.notes || []);
          const rawHtml = row.last_note || latestNote?.note || "";
          const plainText = row.last_note_plain || latestNote?.plain_text || stripHtml(rawHtml);
          const pdfHref = buildNotePdfHref(latestNote?.pdf_url);

          return (
            <NotePreviewCell
              params={params}
              rawHtml={rawHtml}
              plainText={plainText}
              pdfHref={pdfHref}
              expandedCells={expandedCells}
              setExpandedCells={setExpandedCells}
              onShowHtml={showHtmlContent}
            />
          );
        },
      },
      {
        headerName: "Notes Updated",
        field: "note_update",
        width: 150,
        minWidth: 150,
        sortable: true,
        filter: true,
        valueGetter: (params) => params.data?.note_update || null,
        comparator: (a, b) => {
          const da = a ? dayjs(a).valueOf() : 0;
          const db = b ? dayjs(b).valueOf() : 0;
          return da - db;
        },
        cellRenderer: (params) => {
          if (!params.value) {
            return (
              <div className="w-full flex items-center justify-center">
                <span className="text-[11px] text-[var(--fg-muted)]">No notes</span>
              </div>
            );
          }

          const moment = dayjs(params.value);
          const formatted = moment.format("MMM D, YYYY");
          const rel = moment.fromNow();
          const tooltip = `${params.value}${
            params.data?.note_update_text ? ` — ${params.data.note_update_text}` : ""
          }`;

          return (
            <div
              className="w-full flex items-center justify-center text-[12px]"
              title={tooltip}
              style={{ lineHeight: "14px" }}
            >
              <div className="text-center">
                <div className="font-medium text-xs">{formatted}</div>
                <div className="text-[10px] text-[var(--fg-muted)]">({rel})</div>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Sub Status",
        field: "sub_status",
        width: 150,
        minWidth: 150,
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <div className="w-full flex items-center justify-center">
            {renderTruncated(params, params.value?.trim() || "NA")}
          </div>
        ),
      },
      {
        headerName: "Remark",
        field: "remark",
        width: 180,
        minWidth: 180,
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <div className="w-full flex items-center justify-center">
            {renderTruncated(params, params.value || "NA")}
          </div>
        ),
      },
      {
        headerName: "Label",
        field: "label",
        width: 110,
        minWidth: 110,
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <div className="w-full flex items-center justify-center">
            <span className={getLabelClass(params.value)}>
              {String(params.value || "NA").toUpperCase()}
            </span>
          </div>
        ),
      },
      {
        headerName: "Data Updated On",
        field: "updated_date",
        width: 140,
        minWidth: 140,
        sortable: true,
        filter: true,
        valueFormatter: (p) => (p.value ? dayjs(p.value).format("MMM D, YYYY") : ""),
      },
      {
        headerName: "Matched Party",
        field: "matched_party_name",
        minWidth: 180,
        flex: 1,
        cellRenderer: (p) => <span title={p.value || ""}>{safeText(p.value)}</span>,
      },
      { headerName: "ACRIS Doc ID", field: "acris_document_id", width: 150 },
      { headerName: "Doc Type", field: "acris_doc_type", width: 120 },
      {
        headerName: "Latest Deed Date",
        field: "latest_deed_date",
        width: 140,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Amount",
        field: "acris_doc_amount",
        width: 120,
        valueFormatter: (p) => formatMoney(p.value),
      },
      {
        headerName: "PDF",
        field: "deed_pdf_local_path",
        width: 140,
        cellRenderer: (p) => {
          const info = buildPdfLink(p.data);
          if (!info?.href) {
            return <span className="text-xs text-[var(--fg-muted)]">{info?.label || "NA"}</span>;
          }
          return (
            <a
              href={info.href}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[11px] text-[var(--accent)] underline"
            >
              {info.label}
            </a>
          );
        },
      },
      {
        headerName: "Actions",
        field: "id",
        width: 180,
        cellRenderer: (p) => (
          <div className="flex items-center gap-2">
            <Link
              className="ui-btn ui-btn-primary ui-btn-compact"
              to={`/acris-legacy/leads/${p.data.id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              View Detail
            </Link>
            {p.data?.deed_det_url && (
              <a
                href={p.data.deed_det_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ui-btn ui-btn-ghost ui-btn-compact"
              >
                ACRIS
              </a>
            )}
          </div>
        ),
      },
    ],
    [openAddress, expandedCells, expandedSingleId]
  );

  const multipleColumnDefs = useMemo(
    () => [
      {
        headerName: "Name",
        field: "display_name",
        minWidth: 200,
        flex: 1,
        cellClass: "cell-pad-lg",
        cellRenderer: (p) => <span className="block truncate">{safeText(p.value)}</span>,
      },
      {
        headerName: "Records",
        field: "record_count",
        width: 110,
        cellRenderer: (p) => (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
            {p.value || 0} records
          </span>
        ),
      },
      {
        headerName: "Latest Date",
        field: "latest_date",
        width: 130,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Boroughs",
        field: "boroughs",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p) => <span className="truncate">{safeText(p.value)}</span>,
      },
      {
        headerName: "Match",
        field: "match_summary",
        width: 160,
        cellRenderer: (p) => (
          <span className="block max-w-[140px] truncate text-xs text-slate-600" title={p.value || ""}>
            {safeText(p.value, "NA")}
          </span>
        ),
      },
      {
        headerName: "Review",
        field: "__review",
        width: 120,
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const isExpanded = !!params.node?.expanded;
          return (
            <button
              className="ui-btn ui-btn-compact ui-btn-review"
              onClick={(e) => {
                e.stopPropagation();
                try {
                  const node = params.node;
                  const willOpen = !node.expanded;
                  params.api.forEachNode((n) => {
                    if (n && n.expanded) n.setExpanded(false);
                  });
                  node.setExpanded(willOpen);
                  params.api.refreshCells({ force: true });
                  params.api.resetRowHeights();
                } catch (err) {
                  console.warn("toggle expand failed", err);
                }
              }}
            >
              {isExpanded ? (
                <span className="inline-flex items-center gap-1">
                  <FaMinus size={10} /> Hide
                </span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <FaPlus size={10} /> Review
                </span>
              )}
            </button>
          );
        },
      },
    ],
    []
  );

  const onSortChanged = (params) => {
    const model = params.api.getSortModel ? params.api.getSortModel() : [];
    if (!model || model.length === 0) return;
    const next = model[0];
    if (!next?.colId || !next?.sort) return;
    setSortBy(next.colId);
    setSortDir(next.sort);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const onGridReady = (params) => {
    gridRef.current = params.api;

    try {
      const allCols = params.columnApi.getAllColumns() || [];
      const ids = allCols
        .map((c) => c.getColId())
        .filter((id) => id && id !== "property_full_address" && id !== "last_note");
      if (ids.length) params.columnApi.autoSizeColumns(ids, false);
    } catch {}
  };

  return (
    <div className="p-2 md:p-3 lg:p-4 space-y-3">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">
        <div
          className="absolute inset-0 opacity-80 bg-gradient-to-r from-indigo-100/60 via-cyan-100/50 to-transparent"
          aria-hidden
        />
        <div className="relative px-4 py-3 md:px-5 md:py-3 lg:px-6 lg:py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="page-kicker">ACRIS Legacy</p>
              <h1 className="page-title">Probate Leads</h1>
              <p className="page-subtitle">Verified deceased owners (latest owner = 1).</p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="text-xs text-[var(--fg-muted)]">
                Showing {rows.length} of {pagination.total}
              </div>

              {!lockTab && (
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <TabButton active={tab === "single"} icon={FaUser} onClick={() => setTab("single")}>
                    Single Records
                    <span className="tab-count ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold">
                      {tabCounts.single}
                    </span>
                  </TabButton>

                  <TabButton active={tab === "multiple"} icon={FaLayerGroup} onClick={() => setTab("multiple")}>
                    Multiple Records
                    <span className="tab-count ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold">
                      {tabCounts.multiple}
                    </span>
                  </TabButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="filter-panel filter-panel--compact">
        <div className="filter-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
          <input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            onKeyDown={handleEnterApply}
            placeholder="Search name, address, doc ID"
            className="ui-input"
          />

          <select
            value={filters.borough}
            onChange={(e) => setFilters((f) => ({ ...f, borough: e.target.value }))}
            className="ui-select"
          >
            <option value="">All boroughs</option>
            {BOROUGH_OPTIONS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>

          <select
            value={filters.confidence}
            onChange={(e) => setFilters((f) => ({ ...f, confidence: e.target.value }))}
            className="ui-select"
          >
            <option value="">All confidence</option>
            <option value="STRONG">STRONG</option>
            <option value="WEAK">WEAK</option>
            <option value="EXACT">EXACT</option>
            <option value="FUZZY">FUZZY</option>
            <option value="EXACT_ALT_FORMAT">EXACT_ALT_FORMAT</option>
          </select>

          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
            onKeyDown={handleEnterApply}
            className="ui-input"
          />

          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
            onKeyDown={handleEnterApply}
            className="ui-input"
          />
        </div>

        <div className="filter-actions">
          <button onClick={applyFilters} className="ui-btn ui-btn-primary">
            Apply
          </button>
          <button onClick={resetFilters} className="ui-btn">
            Reset
          </button>
        </div>
      </div>

      <div className="surface-card rounded-2xl p-3">
        {error && <div className="text-sm text-rose-600 mb-3">{error}</div>}

        <div className="rounded-2xl border border-[var(--border)] shadow-sm bg-[var(--surface)] overflow-hidden">
          <AppDataGrid
            gridRef={gridRef}
            rowData={rows}
            columnDefs={tab === "multiple" ? multipleColumnDefs : singleColumnDefs}
            loading={loading}
            height={"calc(100vh - 260px)"}
            minWidth={0}
            className="ag-legacy-classic"
            defaultColDef={{
              resizable: true,
              sortable: true,
              filter: true,
              suppressMenu: false,
              minWidth: 100,
              cellClass: "text-sm text-left",
              headerClass: "text-left",
              cellStyle: {
                userSelect: "text",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
            rowHeight={56}
            headerHeight={36}
            onSortChanged={onSortChanged}
            onGridReady={onGridReady}
            suppressRowClickSelection={true}
            rowSelection={tab === "multiple" ? "single" : "multiple"}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No records found.</span>'
            masterDetail={true}
            isRowMaster={() => true}
            detailCellRenderer={tab === "multiple" ? DetailCellRenderer : SingleDetailCellRenderer}
            detailRowAutoHeight={true}
            detailRowHeight={220}
          />
        </div>

        {openAddress &&
          ReactDOM.createPortal(
            <div
              className="fixed z-[999999]"
              style={getTooltipStyle(openAddress.rect)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="rounded-lg border border-slate-200 bg-white shadow-xl p-2 text-xs text-slate-800 whitespace-normal break-words">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-slate-700">Full Address</div>
                  <button
                    className="text-[11px] text-slate-500 hover:text-slate-800"
                    onClick={() => setOpenAddress(null)}
                    type="button"
                  >
                    ✕
                  </button>
                </div>
                <div style={{ userSelect: "text" }}>{openAddress.text}</div>
              </div>
            </div>,
            document.body
          )}

        <div className="pagination-bar flex items-center gap-2 mt-4">
          <button disabled={pagination.page <= 1} onClick={() => setPagination((p) => ({ ...p, page: 1 }))}>
            First
          </button>

          <button
            disabled={pagination.page <= 1}
            onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
          >
            Prev
          </button>

          <span className="text-sm">
            Page {pagination.page} of {pagination.totalPages}
          </span>

          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() =>
              setPagination((p) => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))
            }
          >
            Next
          </button>

          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination((p) => ({ ...p, page: p.totalPages }))}
          >
            Last
          </button>

          <select
            value={pagination.limit}
            onChange={(e) =>
              setPagination((p) => ({ ...p, limit: Number(e.target.value), page: 1 }))
            }
            className="ml-auto ui-select"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {editModalOpen &&
  ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-3xl border border-slate-300">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-[18px] font-semibold text-slate-800">
            {noteModalMode === "add" ? "Add Note" : "Edit Note"}
          </h3>

          <div className="text-xs text-slate-500">
            {editingNote?.created_at
              ? dayjs(editingNote.created_at).format("M/D/YYYY, h:mm:ss A")
              : ""}
          </div>
        </div>

        <div className="p-5">
          <div
            ref={editEditorRef}
            contentEditable
            suppressContentEditableWarning
            className="w-full rounded border border-slate-700 bg-white p-3 text-sm text-slate-800 outline-none"
            style={{
              minHeight: 180,
              maxHeight: 420,
              overflowY: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          />

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={closeNoteModal}
              className="px-4 py-2 border border-slate-300 rounded text-sm font-medium text-slate-700 bg-white hover:bg-slate-50"
              type="button"
              disabled={editSaving}
            >
              Cancel
            </button>

            <button
              onClick={handleSaveNoteModal}
              disabled={editSaving}
              className={`px-4 py-2 rounded text-sm font-medium text-white ${
                editSaving
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
              type="button"
            >
              {editSaving
                ? "Saving..."
                : noteModalMode === "add"
                ? "Add"
                : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )}

      {htmlModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center pt-20 bg-black bg-opacity-40 p-4 overflow-y-auto">
            <div className="bg-white rounded shadow-lg w-full max-w-4xl p-4 max-h-[80vh] overflow-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Last Note — Full HTML</h3>
                <button
                  onClick={() => setHtmlModalOpen(false)}
                  className="px-2 py-1 border rounded text-sm"
                  type="button"
                >
                  Close
                </button>
              </div>

              <div className="prose max-w-full text-sm">
                <div
                  className="bg-[var(--surface-muted)] text-[var(--fg-muted)]"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlForView(htmlModalContent),
                  }}
                />
              </div>

              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() =>
                    copyToClipboard(stripHtml(htmlModalContent), "Copied plain text", "Failed to copy")
                  }
                  className="px-3 py-1 border rounded text-sm"
                  type="button"
                >
                  Copy plain text
                </button>

                <button
                  onClick={() =>
                    copyToClipboard(htmlModalContent, "Copied raw HTML", "Failed to copy")
                  }
                  className="px-3 py-1 border rounded text-sm"
                  type="button"
                >
                  Copy raw HTML
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}